import { ProcessPictureElement } from "@fidelix/fx-margaret";
import { useRef, useState } from "react";
import { DateTime } from "luxon";
import { useParsedSearchParams } from "@fidelix/fx-miranda";
import {
  GraphicHistoryItem,
  GraphicValue,
  HISTORY_REPLAY_PREVIEW_KEY,
  HistoryResolution,
  useGraphicsHistoryPreviewQuery,
  useGraphicsHistoryQuery,
} from "../graphics.queries";
import { queryClient } from "../../utils/query-client";
import { seekLookbackSeconds } from "./history-replay.utils";

export type CachedHistoryData = {
  from: number;
  to: number;
  data: GraphicHistoryItem;
};

export type DataChunk = Pick<CachedHistoryData, "from" | "to"> & {
  fetchInBackground: boolean;
};

export type HistoryDataProps = {
  timestampInSeconds: number;
  pointIds?: number[];
  isPlaying: boolean;
  playbackSpeed: number;
};

export type SelectedPointData = {
  // MargaretId is required to show the checkmark in the process picture
  // Undefined means that checkmark isn't shown at all
  margaretId?: string;
  data?: GraphicHistoryItem;
};

export type SelectionState = {
  isSelectionMode: boolean;
  cached: Record<number, SelectedPointData>;
};

export const defaultReplayFetchPeriodSeconds = 10 * 60;
// Wall-clock seconds of remaining playback at which the next chunk is prefetched
export const playmodeFetchBufferSeconds = 15;

export function findCachedDataset(
  timestampInSeconds: number,
  cachedData: CachedHistoryData[],
) {
  return cachedData.find(
    (dat) => timestampInSeconds >= dat.from && timestampInSeconds <= dat.to,
  );
}

// Returns the parts of [from, to] not covered by cachedData (sorted, non-overlapping)
export function findUncoveredRanges(
  from: number,
  to: number,
  cachedData: CachedHistoryData[],
): [number, number][] {
  const gaps: [number, number][] = [];
  let cursor = from;
  for (const dat of cachedData) {
    if (dat.to < cursor) {
      continue;
    }
    if (dat.from > to) {
      break;
    }
    if (dat.from > cursor) {
      gaps.push([cursor, dat.from]);
    }
    cursor = Math.max(cursor, dat.to);
  }
  if (cursor < to) {
    gaps.push([cursor, to]);
  }
  return gaps;
}

// Determines which time range should be fetched next, if any.
// - If the lookback window before the playhead isn't cached (e.g. user moved the
//   playhead manually), fetch the missing parts of [ts - lookback, ts + 10min].
// - While playing, prefetch the next 10 minutes in the background when close to
//   the end of the current cached range.
export function determineDataChunk(
  timestampInSeconds: number,
  cachedData: CachedHistoryData[],
  isPlaying: boolean,
  playbackSpeed = 1,
): DataChunk | undefined {
  if (!timestampInSeconds || isNaN(timestampInSeconds)) {
    return undefined;
  }

  const gaps = findUncoveredRanges(
    timestampInSeconds - seekLookbackSeconds,
    timestampInSeconds + defaultReplayFetchPeriodSeconds,
    cachedData,
  );

  if (gaps.length > 0 && gaps[0][0] < timestampInSeconds) {
    return {
      from: gaps[0][0],
      to: gaps[gaps.length - 1][1],
      fetchInBackground: false,
    };
  }

  if (!isPlaying) {
    return undefined;
  }

  const current = findCachedDataset(timestampInSeconds, cachedData);
  const bufferSeconds = playmodeFetchBufferSeconds * Math.max(1, playbackSpeed);

  if (!current || timestampInSeconds + bufferSeconds <= current.to) {
    return undefined;
  }

  const next = cachedData.find((dat) => dat.from > current.to);

  return {
    from: current.to,
    to: Math.min(
      current.to + defaultReplayFetchPeriodSeconds,
      next?.from ?? Infinity,
    ),
    fetchInBackground: true,
  };
}

function mergeValues(a: GraphicValue[], b: GraphicValue[]): GraphicValue[] {
  if (a.length === 0) {
    return b;
  }
  if (b.length === 0) {
    return a;
  }
  const [first, second] =
    a[0].timestampJS <= b[0].timestampJS ? [a, b] : [b, a];

  if (first[first.length - 1].timestampJS < second[0].timestampJS) {
    return first.concat(second);
  }

  // Overlapping ranges: sort and drop duplicate samples
  const sorted = first
    .concat(second)
    .sort((x, y) => x.timestampJS.toMillis() - y.timestampJS.toMillis());
  return sorted.filter(
    (v, i) =>
      i === 0 ||
      v.timestampJS.toMillis() !== sorted[i - 1].timestampJS.toMillis(),
  );
}

export function mergeHistoryData(
  a: GraphicHistoryItem,
  b: GraphicHistoryItem,
): GraphicHistoryItem {
  const merged: GraphicHistoryItem = { ...a };
  Object.entries(b).forEach(([key, values]) => {
    merged[key] = merged[key] ? mergeValues(merged[key], values) : values;
  });
  return merged;
}

// Returns a new sorted cache where the fetched chunk is merged together with
// any cached ranges it overlaps or touches
export function addToCachedData(
  newData: GraphicHistoryItem,
  cachedData: CachedHistoryData[],
  dataChunk: Pick<DataChunk, "from" | "to">,
): CachedHistoryData[] {
  let merged: CachedHistoryData = {
    from: dataChunk.from,
    to: dataChunk.to,
    data: newData,
  };
  const result: CachedHistoryData[] = [];

  for (const dat of cachedData) {
    if (dat.to < merged.from || dat.from > merged.to) {
      result.push(dat);
      continue;
    }
    merged = {
      from: Math.min(dat.from, merged.from),
      to: Math.max(dat.to, merged.to),
      data: mergeHistoryData(dat.data, merged.data),
    };
  }

  result.push(merged);
  return result.sort((x, y) => x.from - y.from);
}

export function useHistoryData({
  timestampInSeconds,
  pointIds,
  isPlaying,
  playbackSpeed,
}: HistoryDataProps) {
  const pointIdsKey = pointIds?.join(",") ?? "";
  const cache = useRef<{
    pointIdsKey: string;
    data: CachedHistoryData[];
    resolution?: HistoryResolution;
  }>({
    pointIdsKey,
    data: [],
  });

  if (cache.current.pointIdsKey !== pointIdsKey) {
    cache.current = { pointIdsKey, data: [] };
  }

  const flooredTimestamp = Math.floor(timestampInSeconds);
  const dataChunk = determineDataChunk(
    flooredTimestamp,
    cache.current.data,
    isPlaying,
    playbackSpeed,
  );

  const {
    data: fetchedData,
    isSuccess,
    isFetching: historyIsFetching,
    isError: historyIsError,
  } = useGraphicsHistoryQuery(
    {
      pointIds: pointIds!,
      from: DateTime.fromSeconds(dataChunk?.from || 0),
      to: DateTime.fromSeconds(dataChunk?.to || 0),
    },
    {
      enabled: !!pointIds && pointIds.length > 0 && dataChunk !== undefined,
      staleTime: Infinity,
    },
  );

  // Merge the result (fresh or from the react-query cache) into our ranges.
  // Once merged, the chunk is covered and the next render picks a new chunk.
  if (dataChunk && isSuccess && fetchedData) {
    cache.current.data = addToCachedData(
      fetchedData.history,
      cache.current.data,
      dataChunk,
    );
    cache.current.resolution = fetchedData.resolution;
  }

  const isFetching = dataChunk?.fetchInBackground ? false : historyIsFetching;

  return {
    historyIsFetching: isFetching,
    historyIsError,
    historyData: findCachedDataset(flooredTimestamp, cache.current.data)?.data,
    // Resolution of the most recently fetched chunk
    historyResolution: cache.current.resolution,
  };
}

export type HistoryPreviewDataService = {
  selectionMode: boolean;
  getSelectedPoints: () => SelectedPoints;
  getPreviewDataFromSelectedPoints: () => GraphicHistoryItem;
  setSelectionMode: (newMode: boolean) => void;
  togglePointSelectedStatus: (pointId: number, margaretId: string) => void;
  resetState: () => void;
  isFetching: boolean;
};

type SelectedPoints = Record<string, number | undefined>;

export function useHistoryPreviewData(element?: ProcessPictureElement) {
  const { parsedParams } = useParsedSearchParams({
    to: { type: "string", defaultValue: undefined },
    from: { type: "string", defaultValue: undefined },
  });

  const from = parsedParams.from
    ? DateTime.fromISO(parsedParams.from)
    : DateTime.now();
  const to = parsedParams.to
    ? DateTime.fromISO(parsedParams.to)
    : DateTime.now();

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState<SelectedPoints>({});

  // Find any pointIds we don't have cached data for yet
  const fetchId = getPointIdWithNoData();

  const { isFetching } = useGraphicsHistoryPreviewQuery(
    {
      // NOTE: We will only fetch one point at a time to ensure maximum resolution
      // This should pretty much always be the case
      pointIds: [fetchId!],
      from: from,
      to: to,
    },
    {
      enabled:
        fetchId !== undefined && !!parsedParams.from && !!parsedParams.to,
      staleTime: Infinity,
    },
  );

  function resetState() {
    setSelectionMode(false);
    setSelectedPoints({});
    setProcessPicturePoints({});
  }
  /**
   *
   * @param pointId PointId number from Assets database
   * @param margaretId Ev.detail.fdxpntid from the process picture.
   * @returns
   */

  function getPointIdWithNoData() {
    const selectedIds = Object.values(getSelectedPoints());

    for (let i = 0; i < selectedIds.length; i++) {
      const cachedData = queryClient.getQueryCache().find({
        queryKey: [
          HISTORY_REPLAY_PREVIEW_KEY,
          {
            pointIds: [selectedIds[i]],
            from: from,
            to: to,
          },
        ],
      })?.state?.data;

      // Return the first occurance we don't have cached data yet
      if (!cachedData) {
        return selectedIds[i];
      }
    }
  }

  function togglePointSelectedStatus(pointId: number, margaretId: string) {
    if (!selectionMode) {
      return;
    }

    const updated = { ...selectedPoints };
    const toggleOn =
      !Object.hasOwn(updated, margaretId) || updated[margaretId] === undefined;

    updated[margaretId] = toggleOn ? pointId : undefined;

    setProcessPicturePoints(updated);
    setSelectedPoints(updated);
  }

  function getPreviewDataFromSelectedPoints(): GraphicHistoryItem {
    const selectedIds = getSelectedPoints();

    if (selectedIds.length === 0) {
      return {};
    }

    const data: GraphicHistoryItem = Object.entries(selectedIds).reduce(
      (acc, entry) => {
        const cachedData = queryClient.getQueryCache().find({
          queryKey: [
            HISTORY_REPLAY_PREVIEW_KEY,
            { pointIds: [entry[1]], from: from, to: to },
          ],
        })?.state?.data;

        if (!cachedData) {
          return acc;
        }

        const entryId = entry[0].split(".").at(-1) as string;
        return { ...acc, [entryId]: cachedData[entryId] };
      },
      {},
    );
    return data;
  }

  function getSelectedPoints(): Record<string, number> {
    return Object.keys(selectedPoints).reduce((a, c) => {
      if (selectedPoints[c] !== undefined) {
        return { ...a, [c]: selectedPoints[c] };
      }
      return a;
    }, {});
  }

  function setProcessPicturePoints(points: SelectedPoints) {
    element?.setSelectedPoints(
      Object.keys(points).filter((v) => points[v] !== undefined),
    );
  }

  return {
    selectionMode,
    getPreviewDataFromSelectedPoints,
    setSelectionMode,
    togglePointSelectedStatus,
    resetState,
    getSelectedPoints,
    isFetching,
  } as HistoryPreviewDataService;
}
